import { useState, useEffect, useRef } from 'react';
import {
  Card,
  Tabs,
  Input,
  Button,
  Typography,
  Avatar,
  Badge,
  Space,
  message,
  Modal,
  Tree,
  Spin,
  List,
  Tag,
  Descriptions,
  Divider
} from 'antd';
import {
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  KeyOutlined,
  DashboardOutlined,
  LoadingOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FolderOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useMonitoring } from '../../hooks/useMonitoring';
import MonitoringTree from '../../components/monitoring/MonitoringTree';
import TicketCard from '../../components/tickets/TicketCard';
import CreateTicketModal from '../../components/tickets/CreateTicketModal';
import UpdateTicketModal from '../../components/tickets/UpdateTicketModal';

const { Title, Text } = Typography;

export default function LabSession() {
  const { sessionId: urlSessionId, labId } = useParams();
  const navigate = useNavigate();

  // State
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [labData, setLabData] = useState(null);
  const [aiCharacters, setAiCharacters] = useState([]);
  const [activeTab, setActiveTab] = useState('monitoring');
  const [sessionId, setSessionId] = useState(urlSessionId);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [typingCharacters, setTypingCharacters] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  // Ticket state
  const [tickets, setTickets] = useState([]);
  const [createTicketModalVisible, setCreateTicketModalVisible] = useState(false);
  const [updateTicketModalVisible, setUpdateTicketModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Materials and credentials state
  const [labMaterials, setLabMaterials] = useState([]);
  const [labExercises, setLabExercises] = useState([]);
  const [externalCredentials, setExternalCredentials] = useState([]);
  const [showCredentialPassword, setShowCredentialPassword] = useState({});

  // Refs
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Monitoring hook
  const {
    monitoringHierarchy,
    monitoringStates,
    loading: monitoringLoading,
    updateMonitoringState,
    handleWebSocketUpdate: handleMonitoringWebSocketUpdate
  } = useMonitoring(sessionId, labData?.lab_id);

  useEffect(() => {
    initializeLabSession();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [sessionId, labId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeLabSession = async () => {
    try {
      setLoading(true);

      // If we have a sessionId from URL, load it
      // Otherwise, start a new session
      let response;
      if (sessionId) {
        response = await api.get(`/api/lab-sessions/${sessionId}`);
      } else if (labId) {
        // Start a new session
        response = await api.post('/api/lab-sessions/start', {
          lab_id: labId,
          user_lab_assignment_id: labId // TODO: Get actual assignment ID
        });
      } else {
        throw new Error('No session ID or lab ID provided');
      }

      setLabData(response.data);
      setSessionId(response.data.id);
      setAiCharacters(response.data.ai_characters || []);

      // Initialize WebSocket connection
      connectWebSocket(response.data.id);

      // Load tickets for the session
      const ticketsResponse = await api.get(`/api/lab-sessions/${response.data.id}/tickets`);
      setTickets(ticketsResponse.data || []);

      // Load lab materials, exercises, and credentials
      if (response.data.lab_id) {
        const [materialsRes, exercisesRes, credentialsRes] = await Promise.allSettled([
          api.get(`/labs/${response.data.lab_id}/materials`),
          api.get(`/labs/${response.data.lab_id}/exercises`),
          api.get(`/labs/${response.data.lab_id}/credentials`)
        ]);

        if (materialsRes.status === 'fulfilled') {
          setLabMaterials(materialsRes.value.data || []);
        }
        if (exercisesRes.status === 'fulfilled') {
          setLabExercises(exercisesRes.value.data || []);
        }
        if (credentialsRes.status === 'fulfilled') {
          setExternalCredentials(credentialsRes.value.data || []);
        }
      }

    } catch (error) {
      message.error('Failed to initialize lab session');
      console.error('Lab session error:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = (sid) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Authentication token not found');
        return;
      }

      // Build WebSocket URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = import.meta.env.VITE_WS_URL || window.location.host;
      const wsUrl = `${wsProtocol}//${wsHost}/api/lab-sessions/ws/${sid}?token=${token}`;

      console.log('Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        message.success('Connected to lab session');
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('WebSocket message:', data);

        if (data.type === 'message') {
          // Add message to chat
          setMessages(prev => [...prev, {
            id: data.message_id,
            sender_type: data.sender_type,
            sender_name: data.sender_name || (data.sender_type === 'student' ? 'You' : 'AI'),
            sender_role: data.sender_role,
            message: data.message,
            timestamp: data.timestamp,
            is_read: false
          }]);

          // Play notification sound if from AI character
          if (data.sender_type === 'ai_character') {
            try {
              new Audio('/notification.mp3').play().catch(() => {});
            } catch (e) {
              console.log('Sound notification failed:', e);
            }
          }
        } else if (data.type === 'typing') {
          // Update typing indicators
          setTypingCharacters(prev => {
            const newSet = new Set(prev);
            if (data.is_typing) {
              newSet.add(data.character);
            } else {
              newSet.delete(data.character);
            }
            return newSet;
          });
        } else if (data.type === 'history') {
          // Load chat history
          setMessages(data.messages || []);
        } else if (data.type === 'connected') {
          console.log('WebSocket connection confirmed');
        } else if (data.type === 'error') {
          message.error(data.message || 'An error occurred');
        } else if (data.type === 'pong') {
          // Heartbeat response
          console.log('Heartbeat pong received');
        } else if (data.type === 'monitoring_update' || data.type === 'monitoring_summary') {
          // Handle monitoring updates
          handleMonitoringWebSocketUpdate(data);
        } else if (data.type === 'ticket_created') {
          // Handle new ticket creation
          setTickets(prevTickets => [...prevTickets, data.ticket]);
          message.success(`New ticket created: ${data.ticket.ticket_number}`);
        } else if (data.type === 'ticket_updated') {
          // Handle ticket update
          setTickets(prevTickets =>
            prevTickets.map(ticket =>
              ticket.id === data.ticket.id ? data.ticket : ticket
            )
          );
          message.info(`Ticket ${data.ticket.ticket_number} updated`);
        } else if (data.type === 'tickets_list') {
          // Handle tickets list
          setTickets(data.tickets || []);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        message.error('Connection error occurred');
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        message.warning('Disconnected from lab session');
      };

      wsRef.current = ws;

      // Set up heartbeat
      const heartbeatInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000); // Every 30 seconds

      // Clean up heartbeat on unmount
      return () => clearInterval(heartbeatInterval);

    } catch (error) {
      console.error('WebSocket connection error:', error);
      message.error('Failed to connect to lab session');
    }
  };

  const sendMessage = () => {
    if (!inputMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      if (!wsConnected) {
        message.warning('Not connected to lab session');
      }
      return;
    }

    const messageData = {
      type: 'message',
      message: inputMessage.trim(),
      recipient_character_id: selectedCharacter // null = group chat
    };

    try {
      wsRef.current.send(JSON.stringify(messageData));
      setInputMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('Failed to send message');
    }
  };

  const loadTickets = async () => {
    try {
      const response = await api.get(`/api/lab-sessions/${sessionId}/tickets`);
      setTickets(response.data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
      message.error('Failed to load tickets');
    }
  };

  const handleCreateTicket = async (ticketData) => {
    try {
      const response = await api.post(`/api/lab-sessions/${sessionId}/tickets`, ticketData);
      // Ticket will be added via WebSocket broadcast
      setCreateTicketModalVisible(false);
      // Also request full tickets list to ensure sync
      loadTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      message.error('Failed to create ticket');
      throw error;
    }
  };

  const handleUpdateTicket = async (ticketId, updateData) => {
    try {
      const response = await api.put(`/api/lab-sessions/${sessionId}/tickets/${ticketId}`, updateData);
      // Ticket will be updated via WebSocket broadcast
      setUpdateTicketModalVisible(false);
      setSelectedTicket(null);
      // Also request full tickets list to ensure sync
      loadTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
      message.error('Failed to update ticket');
      throw error;
    }
  };

  const handleTicketStatusUpdate = (ticketId, newStatus) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket) {
      setSelectedTicket(ticket);
      setUpdateTicketModalVisible(true);
    }
  };

  const handleAddTicketUpdate = (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket) {
      setSelectedTicket(ticket);
      setUpdateTicketModalVisible(true);
    }
  };

  const handleDownloadMaterial = async (materialId, filename) => {
    try {
      const response = await api.get(`/labs/${labData.lab_id}/materials/${materialId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Material downloaded successfully');
    } catch (error) {
      message.error('Failed to download material');
      console.error('Error downloading material:', error);
    }
  };

  const toggleCredentialPasswordVisibility = (credentialId) => {
    setShowCredentialPassword(prev => ({
      ...prev,
      [credentialId]: !prev[credentialId]
    }));
  };

  const handleCompleteLabSession = () => {
    Modal.confirm({
      title: 'Complete Lab Session',
      content: 'Are you sure you want to complete this lab? Your session will be submitted for evaluation.',
      okText: 'Complete Lab',
      onOk: async () => {
        try {
          // Trigger evaluation
          message.loading({ content: 'Submitting lab for evaluation...', key: 'evaluation', duration: 0 });
          await api.post(`/api/lab-sessions/${sessionId}/evaluate`);

          message.success({ content: 'Lab submitted for evaluation!', key: 'evaluation', duration: 2 });

          // Show evaluation modal
          Modal.info({
            title: 'Evaluation in Progress',
            content: (
              <div>
                <p>Your lab session has been submitted for evaluation.</p>
                <p>The AI will review your performance based on the configured criteria.</p>
                <p>You can view your results on the dashboard.</p>
              </div>
            ),
            onOk: () => {
              // Close WebSocket
              if (wsRef.current) {
                wsRef.current.close();
              }
              navigate('/student/dashboard');
            }
          });
        } catch (error) {
          message.error({ content: 'Failed to submit lab for evaluation', key: 'evaluation' });
          console.error('Evaluation error:', error);
        }
      }
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabItems = [
    {
      key: 'monitoring',
      label: <span><DashboardOutlined /> Monitoring</span>,
      children: (
        <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Title level={4}>System Monitoring</Title>
          <Text type="secondary">Monitor system status and drill down for details</Text>
          <div style={{ marginTop: 16, flex: 1, overflow: 'auto' }}>
            {monitoringLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin tip="Loading monitoring system..." />
              </div>
            ) : (
              <MonitoringTree
                nodes={monitoringHierarchy}
                monitoringStates={monitoringStates}
                showStatus={true}
                autoExpandAll={true}
                onNodeClick={(node) => {
                  console.log('Selected monitoring node:', node);
                  // TODO: Show detailed view in modal or panel
                }}
              />
            )}
          </div>
        </Card>
      )
    },
    {
      key: 'tickets',
      label: <span><FileTextOutlined /> Tickets</span>,
      children: (
        <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>Support Tickets</Title>
              <Text type="secondary">Create and manage incident tickets</Text>
            </div>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => setCreateTicketModalVisible(true)}
            >
              Create Ticket
            </Button>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">No tickets yet. Create your first ticket to get started.</Text>
              </div>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {tickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onUpdateStatus={handleTicketStatusUpdate}
                    onAddUpdate={handleAddTicketUpdate}
                    isEditable={true}
                  />
                ))}
              </Space>
            )}
          </div>
        </Card>
      )
    },
    {
      key: 'guide',
      label: <span><BookOutlined /> Lab Guide</span>,
      children: (
        <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Title level={4}>Lab Guide & Instructions</Title>
          <Divider />

          {/* Lab Exercises */}
          {labExercises.length > 0 ? (
            <div style={{ flex: 1, overflow: 'auto' }}>
              <Title level={5}>Lab Exercises</Title>
              <List
                dataSource={labExercises.sort((a, b) => a.order - b.order)}
                renderItem={(exercise, index) => (
                  <List.Item key={exercise.id}>
                    <Card
                      size="small"
                      style={{ width: '100%' }}
                      title={
                        <Space>
                          <Badge count={exercise.order} style={{ backgroundColor: '#faad14' }} />
                          <Text strong>{exercise.name}</Text>
                          {exercise.points && <Tag color="blue">{exercise.points} points</Tag>}
                        </Space>
                      }
                    >
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Description">
                          {exercise.description}
                        </Descriptions.Item>
                        <Descriptions.Item label="Instructions">
                          <div style={{ whiteSpace: 'pre-wrap' }}>
                            {exercise.instructions}
                          </div>
                        </Descriptions.Item>
                        {exercise.type && (
                          <Descriptions.Item label="Type">
                            <Tag>{exercise.type}</Tag>
                          </Descriptions.Item>
                        )}
                        {exercise.expected_duration && (
                          <Descriptions.Item label="Expected Duration">
                            <ClockCircleOutlined /> {exercise.expected_duration} minutes
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  </List.Item>
                )}
              />

              {/* Lab Materials */}
              {labMaterials.length > 0 && (
                <>
                  <Divider />
                  <Title level={5}>Lab Materials</Title>
                  <List
                    dataSource={labMaterials}
                    renderItem={(material) => (
                      <List.Item
                        key={material.id}
                        actions={[
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadMaterial(material.id, material.title)}
                          >
                            Download
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<FolderOutlined style={{ fontSize: 24, color: '#faad14' }} />}
                          title={material.title}
                          description={
                            <Space>
                              <Tag>{material.material_type}</Tag>
                              {material.file_size && (
                                <Text type="secondary">
                                  {(material.file_size / 1024).toFixed(2)} KB
                                </Text>
                              )}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Text type="secondary">No lab guide available yet. Check back later or contact your instructor.</Text>
            </div>
          )}
        </Card>
      )
    },
    {
      key: 'credentials',
      label: <span><KeyOutlined /> Credentials</span>,
      children: (
        <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Title level={4}>External System Credentials</Title>
          <Text type="secondary">
            Use these credentials to access external systems required for this lab.
          </Text>
          <Divider />

          {externalCredentials.length > 0 ? (
            <div style={{ flex: 1, overflow: 'auto' }}>
              <List
                dataSource={externalCredentials}
                renderItem={(credential) => (
                  <List.Item key={credential.id}>
                    <Card
                      size="small"
                      style={{ width: '100%' }}
                      title={
                        <Space>
                          <KeyOutlined style={{ color: '#52c41a' }} />
                          <Text strong>{credential.service_name}</Text>
                        </Space>
                      }
                    >
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Service URL">
                          <Text copyable>{credential.service_url}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Username">
                          <Text copyable>{credential.username}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Password">
                          <Space>
                            {showCredentialPassword[credential.id] ? (
                              <>
                                <Text copyable>{credential.password}</Text>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EyeInvisibleOutlined />}
                                  onClick={() => toggleCredentialPasswordVisibility(credential.id)}
                                >
                                  Hide
                                </Button>
                              </>
                            ) : (
                              <>
                                <Text>••••••••</Text>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() => toggleCredentialPasswordVisibility(credential.id)}
                                >
                                  Show
                                </Button>
                              </>
                            )}
                          </Space>
                        </Descriptions.Item>
                        {credential.notes && (
                          <Descriptions.Item label="Notes">
                            <Text type="secondary">{credential.notes}</Text>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Text type="secondary">No credentials configured for this lab.</Text>
            </div>
          )}
        </Card>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" tip="Loading lab session..." />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Bar */}
      <div style={{
        background: '#fff',
        padding: '12px 24px',
        borderBottom: '1px solid #e8e8e8',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {labData?.lab_name || 'Lab Session'}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined /> Session ID: {sessionId?.substring(0, 8)}
            {wsConnected && <Badge status="success" text="Connected" style={{ marginLeft: 16 }} />}
            {!wsConnected && <Badge status="error" text="Disconnected" style={{ marginLeft: 16 }} />}
          </Text>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<CheckCircleOutlined />}
          onClick={handleCompleteLabSession}
          style={{ backgroundColor: '#52c41a' }}
        >
          I'm Done
        </Button>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left 1/3: Chat */}
        <div style={{
          width: '33%',
          borderRight: '1px solid #e8e8e8',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff'
        }}>
          {/* Chat Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e8e8e8',
            background: '#fafafa'
          }}>
            <Title level={5} style={{ margin: 0 }}>
              <TeamOutlined /> AI Team Members
            </Title>
            {aiCharacters.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {aiCharacters.map(char => (
                  <Badge
                    key={char.id}
                    status={typingCharacters.has(char.name) ? "processing" : "default"}
                    text={char.name}
                    style={{ display: 'block', marginBottom: 4 }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            background: '#fafafa'
          }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">No messages yet. Start a conversation!</Text>
              </div>
            )}
            {messages.map((msg, index) => (
              <div
                key={msg.id || index}
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}
              >
                <Avatar style={{
                  backgroundColor: msg.sender_type === 'student' ? '#faad14' : '#1890ff',
                  flexShrink: 0
                }}>
                  {msg.sender_name?.charAt(0) || '?'}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <div>
                    <Text strong>{msg.sender_name || 'Unknown'}</Text>
                    {msg.sender_role && (
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                        {msg.sender_role}
                      </Text>
                    )}
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                      {formatTimestamp(msg.timestamp)}
                    </Text>
                  </div>
                  <div style={{
                    marginTop: 4,
                    padding: '8px 12px',
                    background: msg.sender_type === 'student' ? '#fff7e6' : '#e6f7ff',
                    borderRadius: 8
                  }}>
                    <Text>{msg.message}</Text>
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicators */}
            {Array.from(typingCharacters).map(charName => (
              <div key={charName} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar style={{ backgroundColor: '#1890ff', flexShrink: 0 }}>
                  {charName.charAt(0)}
                </Avatar>
                <Text type="secondary">
                  <LoadingOutlined /> {charName} is typing...
                </Text>
              </div>
            ))}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e8e8e8',
            background: '#fff'
          }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onPressEnter={sendMessage}
                placeholder={wsConnected ? "Type your message..." : "Connecting..."}
                size="large"
                disabled={!wsConnected}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={sendMessage}
                size="large"
                disabled={!wsConnected || !inputMessage.trim()}
              >
                Send
              </Button>
            </Space.Compact>
          </div>
        </div>

        {/* Right 2/3: Tabs */}
        <div style={{ width: '67%', background: '#f0f2f5', overflowY: 'auto' }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{ padding: '16px' }}
          />
        </div>
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        visible={createTicketModalVisible}
        onCancel={() => setCreateTicketModalVisible(false)}
        onCreate={handleCreateTicket}
        aiCharacters={aiCharacters}
      />

      {/* Update Ticket Modal */}
      <UpdateTicketModal
        visible={updateTicketModalVisible}
        onCancel={() => {
          setUpdateTicketModalVisible(false);
          setSelectedTicket(null);
        }}
        onUpdate={handleUpdateTicket}
        ticket={selectedTicket}
      />
    </div>
  );
}
